import React, { useState } from 'react';
import { User, Building, CreditCard, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const CertificateForm = () => {
  const { t, currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    type: 'personal',
    name: '',
    company: '',
    website: '',
    sector: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const normalizeWebsite = (value) => {
    // Strip protocol prefix for storage, keep as entered
    return value.replace(/^https?:\/\//i, '');
  };

  const validateWebsite = (value) => {
    const cleaned = value.replace(/^https?:\/\//i, '');
    // Accept domain.tld, www.domain.tld, domain.co.tr, domain.com.tr etc.
    const domainPattern = /^(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})*([/?#].*)?$/;
    return domainPattern.test(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate website without requiring http/https
    if (formData.website && !validateWebsite(formData.website)) {
      alert(currentLanguage === 'de'
        ? 'Bitte geben Sie eine gültige Website-Adresse ein (z.B. erma.com)'
        : currentLanguage === 'en'
        ? 'Please enter a valid website address (e.g. erma.com)'
        : 'Lütfen geçerli bir web sitesi adresi girin (ör. erma.com)');
      return;
    }

    setIsSubmitting(true);
    
    // Save submission to localStorage for admin panel
    try {
      const submissions = JSON.parse(localStorage.getItem('certificateFormSubmissions') || '[]');
      submissions.unshift({
        id: Date.now(),
        ...formData,
        website: normalizeWebsite(formData.website),
        submittedAt: new Date().toISOString(),
        status: 'new'
      });
      localStorage.setItem('certificateFormSubmissions', JSON.stringify(submissions));
    } catch (err) {
      console.warn('Could not save submission:', err);
    }

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      // Reset form
      setFormData({
        type: 'personal',
        name: '',
        company: '',
        website: '',
        sector: '',
        email: '',
        phone: '',
        message: ''
      });
    }, 2000);
  };

  const sectors = [
    t('form-sector-tech'),
    t('form-sector-ecommerce'),
    t('form-sector-finance'),
    t('form-sector-health'),
    t('form-sector-education'),
    t('form-sector-tourism'),
    t('form-sector-media'),
    t('form-sector-other')
  ];

  // Dynamic placeholders for email and phone
  const emailPlaceholders = {
    tr: 'ornek@email.com',
    de: 'beispiel@email.de',
    en: 'example@email.co.uk',
  };
  const phonePlaceholders = {
    tr: '+90 555 123 4567',
    de: '+49 1512 3456789',
    en: '+44 7123 456789',
  };

  if (isSubmitted) {
    return (
      <section id="certificate-form" className="section-padding bg-neutral-50">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-neutral-800 mb-4">
                {t('form-success-title')}
              </h2>
              <p className="text-neutral-600 mb-6">
                {t('form-success-message')}
              </p>
              <div className="bg-primary-50 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-neutral-800 mb-3">{t('form-next-steps')}</h3>
                <div className="space-y-2 text-sm text-neutral-600">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>{t('form-step-1')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>{t('form-step-2')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>{t('form-step-3')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>{t('form-step-4')}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsSubmitted(false)}
                className="btn-primary"
              >
                {t('form-new-application')}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="certificate-form" className="section-padding bg-neutral-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-800 mb-6">
            {t('get-certificate')}
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('form-subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Application Type */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-3">
                      {t('form-application-type')}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: 'personal' }))}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          formData.type === 'personal'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-neutral-200 hover:border-primary-300'
                        }`}
                      >
                        <User className="h-6 w-6 mx-auto mb-2" />
                        <div className="font-medium">{t('form-type-personal')}</div>
                        <div className="text-sm text-neutral-600">{t('form-type-personal-desc')}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: 'corporate' }))}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          formData.type === 'corporate'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-neutral-200 hover:border-primary-300'
                        }`}
                      >
                        <Building className="h-6 w-6 mx-auto mb-2" />
                        <div className="font-medium">{t('form-type-corporate')}</div>
                        <div className="text-sm text-neutral-600">{t('form-type-corporate-desc')}</div>
                      </button>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        {formData.type === 'personal' ? t('form-name') : t('form-authority-person')}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={formData.type === 'personal' ? t('form-name-placeholder') : t('form-authority-placeholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        {t('form-email')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={emailPlaceholders[currentLanguage] || emailPlaceholders.tr}
                      />
                    </div>
                  </div>

                  {/* Company Information */}
                  {formData.type === 'corporate' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          {t('form-company-name')}
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder={t('form-company-placeholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          {t('form-sector')}
                        </label>
                        <select
                          name="sector"
                          value={formData.sector}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">{t('form-sector-select')}</option>
                          {sectors.map((sector, index) => (
                            <option key={index} value={sector}>{sector}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Website Information */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('form-website')}
                    </label>
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={currentLanguage === 'de' ? 'beispiel.de oder beispiel.com' : currentLanguage === 'en' ? 'example.com or example.co.uk' : 'ornek.com veya ornek.com.tr'}
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      {currentLanguage === 'de'
                        ? 'Ohne http:// oder https:// eingeben, z.B.: firma.de, firma.com.tr'
                        : currentLanguage === 'en'
                        ? 'Enter without http:// or https://, e.g.: company.com, company.co.uk'
                        : 'http:// veya https:// olmadan girin, ör: firma.com, firma.com.tr'}
                    </p>
                  </div>

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        {t('form-phone')}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={phonePlaceholders[currentLanguage] || phonePlaceholders.tr}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        {t('form-message')}
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('form-message-placeholder')}
                      ></textarea>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>{t('form-submitting')}</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5" />
                        <span>{t('form-submit')}</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-neutral-800 mb-4">
                  {t('form-benefits-title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-neutral-800">{t('form-benefit-1-title')}</h4>
                      <p className="text-sm text-neutral-600">{t('form-benefit-1-desc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-neutral-800">{t('form-benefit-2-title')}</h4>
                      <p className="text-sm text-neutral-600">{t('form-benefit-2-desc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-neutral-800">{t('form-benefit-3-title')}</h4>
                      <p className="text-sm text-neutral-600">{t('form-benefit-3-desc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-semibold mb-4">
                  {t('form-process-title')}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">1</div>
                    <span className="text-sm">{t('form-process-1')}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">2</div>
                    <span className="text-sm">{t('form-process-2')}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">3</div>
                    <span className="text-sm">{t('form-process-3')}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">4</div>
                    <span className="text-sm">{t('form-process-4')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificateForm; 